/**
 * Corte de spans na saída para o Sentry: mantém os spans que se olham para
 * diagnosticar e descarta os que só existem porque a instrumentação padrão os
 * cria.
 *
 * MOTIVO, com o que foi medido na organização em 10/09/2026 (30 dias):
 * 229.513.869 spans, TODOS deste app — o painel do monorepo não gera nenhum.
 * São ~124 spans por carregamento de página (1.846.365 carregamentos), e nada
 * disso foi pedido: o `browserTracingIntegration()` é usado sem opções e
 * instrumenta tudo o que a Performance API do navegador expõe. Repartição por
 * operação, no mesmo período:
 *
 *   http.client              44,6 M   ← mantido (latência e falha de API)
 *   mark                     44,1 M   ← descartado
 *   measure                  31,4 M   ← descartado
 *   resource.script          26,7 M   ← descartado
 *   resource.css             13,3 M   ← descartado
 *   ui.long-animation-frame  12,3 M   ← descartado
 *   ui.angular.routing       11,6 M   ← mantido (troca de rota)
 *   resource.img             10,0 M   ← descartado
 *   navigation                9,8 M   ← mantido (é a própria transação)
 *   resource.other            8,3 M   ← descartado
 *   paint                     2,5 M   ← mantido (FCP/LCP)
 *   pageload                  1,85 M  ← mantido (é a própria transação)
 *   browser.*                 5,5 M   ← mantido (abertura do carregamento)
 *
 * O corte remove ~146 M dos 229,5 M — cerca de 64% — sem mexer em nada do que
 * se usa: quanto demorou a carregar, para onde navegou, que chamada de API
 * falhou e quanto tempo levou.
 *
 * DUAS COISAS QUE **NÃO** SE PERDEM, e que costumam ser a preocupação:
 *
 *  - As métricas web (LCP, FCP, CLS, TTFB) viajam em `event.measurements`, não
 *    em spans. Filtrar `event.spans` não lhes toca.
 *  - A transação em si — o `pageload` ou o `navigation` — é o próprio evento, e
 *    não está dentro de `event.spans`. Nenhum filtro aqui a pode descartar; o
 *    que se corta são os filhos.
 *
 * O QUE SE PERDE, dito de frente: o detalhe por recurso (`resource.*`), ou seja
 * qual script/folha de estilo/imagem demorou mais no arranque a frio. A abertura
 * do carregamento (`browser.*`) e o `paint` continuam a dizer *que* o arranque
 * está lento; deixa de se ver *qual ficheiro* o segura. Se algum dia essa
 * investigação voltar a ser precisa, o caminho é ligar `resource.script` aqui
 * por um período curto — e não desligar o filtro todo.
 *
 * A lista é de INCLUSÃO, não de exclusão, e é de propósito: uma versão nova do
 * SDK que passe a instrumentar mais uma coisa entra em silêncio numa lista de
 * exclusão e aparece na fatura no mês seguinte. Aqui, entra apenas o que
 * alguém escreveu que quer ver.
 */

/**
 * Operações mantidas. `browser.` é comparado por prefixo porque o SDK reparte a
 * abertura do carregamento em vários spans irmãos (`browser.request`,
 * `browser.response`, `browser.domContentLoadedEvent`, `browser.connect`, …) e
 * a lista deles muda entre versões — o que interessa é a família inteira.
 */
const OPS_MANTIDAS = new Set([
  'pageload',
  'navigation',
  'ui.angular.routing',
  'http.client',
  'paint',
]);

/** Prefixo mantido por família, pelo motivo descrito em {@link OPS_MANTIDAS}. */
const PREFIXO_MANTIDO = 'browser.';

/**
 * Recorte estrutural do span: só a operação é lida daqui. Como no
 * `sentry-event-throttle.ts`, o tipo não é importado do SDK — `@sentry/core`
 * não é dependência declarada do app e o `@sentry/angular` não reexporta estes
 * tipos.
 */
interface ISentrySpanShape {
  op?: string;
}

/** Recorte estrutural da transação: só a lista de spans filhos é tocada. */
interface ISentryTransactionShape {
  spans?: ISentrySpanShape[];
}

/** `true` quando o span é um dos que se olham para diagnosticar. */
function spanUtil(span: ISentrySpanShape): boolean {
  const op = span.op;
  if (!op) return false;

  return OPS_MANTIDAS.has(op) || op.startsWith(PREFIXO_MANTIDO);
}

/**
 * Devolve a transação sem os spans de ruído. Copia em vez de mutar: o evento
 * ainda pertence ao SDK, que o entrega a seguir aos processadores das
 * integrações.
 *
 * Uma transação sem `spans` — ou que fique sem nenhum filho útil — continua a
 * ser enviada: ela própria é o `pageload`/`navigation`, e é dela que saem a
 * duração e as métricas web.
 */
export function dropNoisySpans<T extends ISentryTransactionShape>(event: T): T {
  if (!event.spans?.length) return event;

  return { ...event, spans: event.spans.filter(spanUtil) };
}
